import React from 'react';
import { useNotifications } from '@doveit/bricks';
import { Feedback } from '../../../providers/api/dtos';
import FeedbackForm, { FeedbackFormModel, FeedbackFormProps } from '../../components/feedback-form/FeedbackForm';
import { updateFeedback } from '../../../providers/api/feedback/feedbackProvider';
import { FeedbackStatus } from '../../../domain/types';

export interface UpdateFeedbackProps {
  feedback: Feedback,
  newStatus?: FeedbackStatus,
  disabled?: boolean,
  formRef?: FeedbackFormProps['innerRef'],
  onSuccess?: (updatedFeedback: Feedback) => void,
  onError?: VoidFunction,
}

export const UPSERT_FEEDBACK_ERROR_MESSAGE = 'Non è stato possibile aggiornare la survey.';
export const UPSERT_FEEDBACK_SUCCESS_MESSAGE = 'Operazione effettuata con successo';

const UpdateFeedback: React.FC<UpdateFeedbackProps> = ({
  feedback,
  newStatus,
  formRef,
  disabled,
  onSuccess,
  onError,
}) => {
  const { addSuccess, addError } = useNotifications();
  const [isSaving, setIsSaving] = React.useState(false);

  const onSubmit = React.useCallback(async (values: FeedbackFormModel) => {
    setIsSaving(true);

    try {
      const payload: Feedback = {
        ...feedback,
        id: feedback.id!,
        status: newStatus || feedback.status,
        survey: {
          ...feedback.survey,
          id: feedback.survey!.id!,
          name: feedback.survey!.name!,
          score: feedback.survey!.score!,
          submittedAt: feedback.survey!.submittedAt ? feedback.survey!.submittedAt : new Date().toISOString(),
          answers: (feedback.survey?.answers || []).map((answer) => ({
            ...answer,
            value: answer.type === 'RATING' ? parseInt(values.answers[answer.questionId], 10) : values.answers[answer.questionId],
          })),
        },
        notes: values.notes || undefined,
      };

      const updatedFeedback = await updateFeedback(feedback.id!, payload);

      setIsSaving(false);
      addSuccess(UPSERT_FEEDBACK_SUCCESS_MESSAGE);
      onSuccess?.(updatedFeedback);
    } catch (error) {
      setIsSaving(false);
      addError(UPSERT_FEEDBACK_ERROR_MESSAGE);
      onError?.();
    }
  }, [addError, addSuccess, feedback, newStatus, onError, onSuccess]);

  if (!feedback.survey?.id) {
    throw new Error('Survey ID is missing');
  }

  return (
    <FeedbackForm
      onSubmit={onSubmit}
      disabled={disabled}
      loading={isSaving}
      answers={feedback.survey?.answers || []}
      notes={feedback.notes}
      innerRef={formRef}
    />
  );
};

export default UpdateFeedback;
